import {Injectable, inject} from '@angular/core';
import {AppLogger, AppLoggerService} from '../../core/logging';
import {AppEventsService} from '../app-events/app-events.service';
import {DialogAction, DialogAffected} from './dialog.models';

export type ActionOutcome =
  | {kind: 'noop'}
  | {kind: 'refreshed'}
  | {kind: 'unsupported'; message: string}
  | {kind: 'error'; message: string};

export interface ActionExecutionContext {
  householdId: number;
}

/**
 * Dispatches an already-decided backend action and reports the outcome — it holds no
 * domain logic of its own. Which store, which stock instance, or which shopping list an
 * action applies to is a backend decision; if a payload isn't directly executable by an
 * existing endpoint without the client inferring something, it's unsupported here.
 *
 * `action.type` is an open string — unknown types must degrade gracefully, never throw.
 */
@Injectable({providedIn: 'root'})
export class ActionExecutor {
  private readonly appEvents = inject(AppEventsService);
  private readonly log: AppLogger = inject(AppLoggerService).createForClass(ActionExecutor);

  async handle(
    action: DialogAction,
    _ctx: ActionExecutionContext,
    affected?: DialogAffected[]
  ): Promise<ActionOutcome> {
    if (action.executed) {
      this.notifyRefresh(action.type, affected);
      return {kind: 'refreshed'};
    }

    try {
      switch (action.type) {
        case 'query_stock':
        case 'product_question':
          // The backend never actually attaches an `action` for these — the answer is
          // entirely in `message`. Handled here defensively in case that ever changes.
          return {kind: 'noop'};
        default:
          // Includes 'remove_stock' / 'add_stock' / 'add_to_shopping_list' with
          // `executed: false`: today's payloads don't carry enough to execute them
          // without the client guessing a store, a stock instance, or a shopping list,
          // which is exactly the inference this class must not do. Once the backend
          // switches these to server-side execution, they'll arrive as `executed: true`
          // and take the fast path above instead.
          return {
            kind: 'unsupported',
            message: 'Diese Aktion wird in dieser App-Version noch nicht unterstützt.',
          };
      }
    } catch (err) {
      this.log.error('Action execution failed', err, {actionType: action.type});
      return {kind: 'error', message: 'Die Aktion konnte nicht ausgeführt werden.'};
    }
  }

  /**
   * Prefers `affected` — which resource(s) actually changed — over guessing from
   * `action.type`. Falls back to the `action.type` heuristic only when `affected` is
   * absent, for compatibility with an older backend. An unrecognized `resource` is
   * ignored, not treated as an error.
   */
  private notifyRefresh(actionType: string, affected?: DialogAffected[]): void {
    if (affected?.length) {
      for (const {resource} of affected) {
        if (resource === 'stock') this.appEvents.notifyStockChanged();
        else if (resource === 'shopping_list') this.appEvents.notifyShoppingListChanged();
        else if (resource === 'recipe') this.appEvents.notifyRecipeChanged();
      }
      return;
    }

    if (actionType === 'add_to_shopping_list') {
      this.appEvents.notifyShoppingListChanged();
    } else if (actionType === 'remove_stock' || actionType === 'add_stock') {
      this.appEvents.notifyStockChanged();
    }
  }
}
